import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';
import stripAnsi from 'strip-ansi';
import { isExpoSDKUpgradeError } from '~/lib/error-handling';
import { frameworks } from '~/lib/persistence/frameworks';
import { ShellAction, StartAction } from '~/lib/runtime/actions';
import { workbenchStore } from '~/lib/stores/workbench';
import { addWorkDir } from '~/utils/workDir';

const dialogOpen = atom(false);
const previewURL = atom('');
const triggerCount = atom(0);
const previewElement = atom<Element | undefined>(undefined);

/**
 * TODO Remove this listener when expo uses xdg-open to trigger the QR code.
 *
 * @see ~/lib/webcontainer/events.ts
 */
const metroPattern = /Metro waiting on\s+(exp:\/\/\S+)/;

workbenchStore.addTerminalOutputListener((output) => {
  if (!output.includes('Metro')) {
    return;
  }

  const link = stripAnsi(output).match(metroPattern);

  if (link?.[1]) {
    setDevicePreviewURL(link[1]);

    // TODO when xdg-open is supported, move this increment inside setDevicePreviewURL
    triggerCount.set(triggerCount.get() + 1);
  }
});

const currentSdkVersion = '^53.0.0';

let expoSdkUpgradeProcess: ReturnType<typeof workbenchStore.runShellAction> | null = null;

workbenchStore.addTerminalOutputListener(async (output) => {
  if (workbenchStore.framework.get()?.name === 'expo' && isExpoSDKUpgradeError(output)) {
    if (expoSdkUpgradeProcess) {
      return;
    }

    const npmrcPath = addWorkDir('.npmrc');
    const npmrc = workbenchStore.files.get()[npmrcPath];

    // lucide-react-native does not support react-19 in its package.json so we have to do --legacy-peer-deps
    if (!(npmrc && npmrc.type === 'file' && npmrc.content.includes('legacy-peer-deps=true'))) {
      const content = npmrc?.type === 'file' ? `\n${npmrc.content}` : '';
      workbenchStore.writeFile(npmrcPath, {
        content: `legacy-peer-deps=true${content}`,
        type: 'file',
      });
    }

    const shellActionId = 'expo-sdk-upgrade';

    const shellAction = new ShellAction(
      shellActionId,
      shellActionId,
      shellActionId,
      `npx expo install expo@${currentSdkVersion} --fix && npx @bycedric/expo-install --fix`,
    );

    expoSdkUpgradeProcess = workbenchStore.runShellAction(shellAction);

    await expoSdkUpgradeProcess.exit.then(expoSdkUpgradeProcess!.done);

    const artifactId = 'expo-sdk-upgrade-start';
    workbenchStore.addArtifact({
      id: artifactId,
      slug: artifactId,
      title: 'Starting Expo',
      messageId: artifactId,
    });

    const startAction = new StartAction(
      artifactId,
      artifactId,
      artifactId,
      false,
      frameworks.find((framework) => framework.name === 'expo')!,
    );

    workbenchStore.addAction(
      {
        artifactId,
        action: startAction,
        messageId: artifactId,
      },
      true,
    );

    workbenchStore.runAction({
      artifactId,
      action: startAction,
      messageId: artifactId,
    });

    return;
  }
});

export function useDevicePreview() {
  const open = useStore(dialogOpen);
  const url = useStore(previewURL);
  const count = useStore(triggerCount);

  return { open, url, count };
}

export function useHasDevicePreviewURL() {
  return useStore(previewURL);
}

export function usePreviewElement() {
  return useStore(previewElement);
}

export function openDevicePreviewURL() {
  dialogOpen.set(true);
}

export function closeDevicePreviewURL() {
  dialogOpen.set(false);
}

export function setDevicePreviewURL(url: string) {
  if (url.length > 0) {
    dialogOpen.set(true);
    previewURL.set(url);
  }
}

export function setPreviewElement(node: Element | null) {
  previewElement.set(node ?? undefined);
}
